import {
  objectFitCoverImageStyles,
  SDUIImage,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Image/SDUIImage";
import { getCssAlignmentValue } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/getCssAlignmentValue";
import {
  convertServerResponsivePropertyToClientResponsiveProperty,
  getResponsiveValueAtIndex,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIBannerTextContent } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Banner/__internal__/BannerTextContent/SDUIBannerTextContent";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { IBannerComponent } from "@faire/web-api--source/indigofair/data/IBannerComponent";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { Column } from "@faire/web/slate/Layout";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { cn } from "@faire/web/tools/cn";
import React from "react";

export const ANNOUNCEMENT_BANNER_HEIGHTS: ResponsiveValues<number> = {
  mobileAndAbove: 200,
  tabletAndAbove: 240,
  desktopAndAbove: 288,
};

const MAX_BANNER_FULL_WIDTH_DESKTOP_IMAGE_WIDTH = 1184;

const OPTIMIZE_IMAGE_OPTIONS = {
  mobileAndAbove: {
    height: ANNOUNCEMENT_BANNER_HEIGHTS.mobileAndAbove,
  },
  tabletAndAbove: {
    height: ANNOUNCEMENT_BANNER_HEIGHTS.tabletAndAbove,
  },
  desktopAndAbove: {
    height: ANNOUNCEMENT_BANNER_HEIGHTS.desktopAndAbove,
    width: MAX_BANNER_FULL_WIDTH_DESKTOP_IMAGE_WIDTH,
  },
};

const varMinHeightMobile: DynamicVariable<"--f-sdui-ab-min-h-m"> = {
  variable: "--f-sdui-ab-min-h-m",
  className: "min-h-(--f-sdui-ab-min-h-m)",
};

const varMinHeightTablet: DynamicVariable<"--f-sdui-ab-min-h-md"> = {
  variable: "--f-sdui-ab-min-h-md",
  className: "md:min-h-(--f-sdui-ab-min-h-md)",
};

const varMinHeightDesktop: DynamicVariable<"--f-sdui-ab-min-h-lg"> = {
  variable: "--f-sdui-ab-min-h-lg",
  className: "lg:min-h-(--f-sdui-ab-min-h-lg)",
};

type SDUIAnnouncementBannerProps = {
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  variant: "standard" | "constrained";
};

type ExternalFacingAnnouncementBannerType = Omit<
  SDUIAnnouncementBannerProps,
  "variant"
>;
export const SDUIDefaultAnnouncementBannerRenderer = (
  props: ExternalFacingAnnouncementBannerType
) => {
  return <BaseSDUIAnnouncementBannerRenderer {...props} variant="standard" />;
};

export const SDUIConstrainedWidthAnnouncementBannerRenderer = (
  props: ExternalFacingAnnouncementBannerType
) => {
  return (
    <BaseSDUIAnnouncementBannerRenderer {...props} variant="constrained" />
  );
};

const BaseSDUIAnnouncementBannerRenderer = ({
  bannerComponent,
  loggingData,
  renderBehaviour,
  variant,
}: SDUIAnnouncementBannerProps) => {
  const { images, responsive_images, vertical_content_alignment } =
    bannerComponent;

  const responsiveImages =
    convertServerResponsivePropertyToClientResponsiveProperty(
      images,
      (img) => img,
      responsive_images
    );

  return (
    <Column
      className={cn(
        "relative w-full p-4 md:p-16 lg:p-10",
        varMinHeightMobile.className,
        varMinHeightTablet.className,
        varMinHeightDesktop.className
      )}
      style={{
        ...assignInlineVars({
          [varMinHeightMobile.variable]: `${ANNOUNCEMENT_BANNER_HEIGHTS.mobileAndAbove}px`,
          [varMinHeightTablet.variable]: `${ANNOUNCEMENT_BANNER_HEIGHTS.tabletAndAbove}px`,
          [varMinHeightDesktop.variable]: `${ANNOUNCEMENT_BANNER_HEIGHTS.desktopAndAbove}px`,
        }),
        justifyContent: `${getCssAlignmentValue(
          vertical_content_alignment,
          "center"
        )}`,
      }}
    >
      <SDUIImage
        responsiveImage={getResponsiveValueAtIndex(
          responsiveImages,
          0,
          IImage.build()
        )}
        optimizeImageOptions={OPTIMIZE_IMAGE_OPTIONS}
        renderBehaviour={renderBehaviour}
        className="absolute top-0 left-0 z-0 h-full w-full"
        imageStyles={objectFitCoverImageStyles}
      />
      <SDUIBannerTextContent
        className={cn(
          "relative z-[1] items-start text-left",
          variant === "constrained" ? "w-[70%]" : "w-1/2"
        )}
        bannerComponent={bannerComponent}
        loggingData={loggingData}
      />
    </Column>
  );
};
